/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  error: unknown;
}

/**
 * The last line of defence for a route that throws while rendering.
 *
 * Until this existed the application had no error boundary at all. The only
 * class implementing `getDerivedStateFromError` was LazyErrorBoundary, which
 * deliberately rethrows anything that is not a chunk-load failure - and nothing
 * above it caught the rethrow, so a single bad render unmounted the whole app to
 * a blank white page with no route, no navigation and no explanation.
 *
 * This sits *outside* LazyErrorBoundary on purpose. The chunk boundary gets
 * first refusal, so a deploy that moved a chunk still recovers silently; what it
 * rethrows is a genuine application error, and lands here.
 *
 * One thing not to "fix" later: if a page throws while an undoable write is
 * held, useUndoableWrite's cleanup commits it. That is its documented contract -
 * a decision the steward explicitly made should not evaporate because an
 * unrelated render failed - and it is deliberate, not an oversight.
 */

export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // The stack belongs in the console, not on screen. A steward cannot act on
    // a component trace, and showing one turns a recoverable page into
    // something that looks broken beyond use.
    console.error(error, errorInfo.componentStack);
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 10,
              }}
            >
              <Typography variant={"h4"} align={"center"}>
                Something went wrong on this page
              </Typography>
              {/*
                These surfaces write to a customer's CRM, so the first thing a
                steward needs to know is whether the thing they just clicked
                landed. It did not: this is a render failure, and a render
                cannot write.
              */}
              <Typography
                variant={"body1"}
                align={"center"}
                color={"text.secondary"}
              >
                The page stopped before it could finish. Nothing has been
                changed in your CRM.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {/* Neither of these commits anything, so neither is contained. */}
                <Button
                  variant={"outlined"}
                  onClick={() => window.location.reload()}
                >
                  Reload this page
                </Button>
                <Button
                  color={"inherit"}
                  onClick={() => {
                    // A hard assignment, not a router navigation: the tree this
                    // boundary wraps is already broken, and re-rendering into it
                    // would throw again.
                    window.location.href =
                      require("../../utils/uiPreference").getPreferredDashboardPath();
                  }}
                >
                  Back to dashboard
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
}
